import { isActiveGroup } from 'sonata-static-storage/lib/helpers/itemsGrouping.helpers';

import { createCartographyGroups } from './cartographyGroupCreation';
import { deleteGroups, detachGroupsFromItems } from './groupDeletion';
import { createGroupId, toId } from './groupIds';
import { addItems, syncDefaultGroupMembership } from './groupMembership';
import { findGroupsEmptiedBy } from './groupPruning';
import { getMemberIds } from './groupQueries';
import { isDefaultGroup } from './groupRules';

import type {
  AdGroupRefs,
  GroupableItem,
  GroupChange,
  GroupChangeContext,
  GroupChangeOutcome,
  GroupFamily,
  GroupingState,
  GroupLike,
  TemporalPacingLike
} from './grouping.types';
import type { ItemsResult } from './groupMembership';

type CreateChange = Omit<Extract<GroupChange, { type: 'create' }>, 'type'>;
type UpdateChange = Extract<GroupChange, { type: 'update' }>;

const noReferences = { ads: 0, temporalPacing: 0 };

export { addItems };

/** Removes the group from the given items (screens fall back to the default group when left without one). */
export function removeItems<G extends GroupLike, I extends GroupableItem>(
  state: Pick<GroupingState<G, I>, 'groups' | 'items'>,
  family: GroupFamily,
  groupId: string,
  itemIds: string[]
): ItemsResult<I> {
  return detachGroupsFromItems({ family, groupIds: [groupId], groups: state.groups, itemIds, items: state.items });
}

/** Renames (trimmed) and/or recolors a group. */
export function updateGroup<G extends GroupLike>(
  groups: G[],
  groupId: string,
  changes: { color?: string; name?: string }
): G[] {
  return groups.map(group =>
    toId(group._id) === groupId
      ? {
          ...group,
          color: changes.color ?? group.color,
          name: changes.name === undefined ? group.name : changes.name.trim()
        }
      : group
  );
}

/** Creates a group (id minted here) and assigns it to the given items. */
export function createGroup<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(
  state: GroupingState<G, I, A, T>,
  { color, itemIds, name }: CreateChange,
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  const groupId = createGroupId();
  const group = context.buildGroup({ _id: groupId, color, name: name.trim() });
  const added = addItems(state, context.family, groupId, itemIds);
  return {
    affectedItemIds: added.affectedItemIds,
    createdGroupId: groupId,
    deletedGroups: [],
    references: noReferences,
    state: { ...state, groups: [...state.groups, group], items: added.items }
  };
}

function applyUpdate<G extends GroupLike, I extends GroupableItem, A extends AdGroupRefs, T extends TemporalPacingLike>(
  state: GroupingState<G, I, A, T>,
  { addItemIds = [], color, groupId, name, removeItemIds = [] }: UpdateChange,
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  const groups = updateGroup(state.groups, groupId, { color, name });
  const added = addItems(state, context.family, groupId, addItemIds);
  const removed = removeItems({ groups, items: added.items }, context.family, groupId, removeItemIds);
  const next = { ...state, groups, items: removed.items };
  const affectedItemIds = [...added.affectedItemIds, ...removed.affectedItemIds];

  if (getMemberIds(context.family, next.items, groupId).length > 0) {
    return { affectedItemIds, deletedGroups: [], references: noReferences, state: next };
  }
  // An edit that leaves the group empty deletes it, with the same rules as an explicit delete.
  const deletion = deleteGroups(next, [groupId], context);
  return { ...deletion, affectedItemIds: [...new Set([...affectedItemIds, ...deletion.affectedItemIds])] };
}

/** Resolves a change on its own, before the default group sync. */
function resolveGroupChange<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(
  state: GroupingState<G, I, A, T>,
  change: GroupChange,
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  if (change.type === 'create') {
    return createGroup(state, change, context);
  }
  if (change.type === 'createCartography') {
    return createCartographyGroups(state, change, context);
  }
  if (change.type === 'deleteItems') {
    // Same rules as an edit that leaves a group empty
    return deleteGroups(state, findGroupsEmptiedBy(context.family, state, change.itemIds), context);
  }
  const target = state.groups.find(group => toId(group._id) === change.groupId);
  if (!target || !isActiveGroup(target) || isDefaultGroup(context.family, target)) {
    return { affectedItemIds: [], deletedGroups: [], references: noReferences, state };
  }
  switch (change.type) {
    case 'addItems': {
      const added = addItems(state, context.family, change.groupId, change.itemIds);
      return {
        affectedItemIds: added.affectedItemIds,
        deletedGroups: [],
        references: noReferences,
        state: { ...state, items: added.items }
      };
    }
    case 'update':
      return applyUpdate(state, change, context);
    default:
      return deleteGroups(state, [change.groupId], context);
  }
}

/**
 * Single entry point resolving any {@link GroupChange} into the next state (pure, immutable).
 * Screens are then re-synced with the default group, whose members are never edited by hand.
 */
export function applyGroupChange<
  G extends GroupLike,
  I extends GroupableItem,
  A extends AdGroupRefs,
  T extends TemporalPacingLike
>(
  state: GroupingState<G, I, A, T>,
  change: GroupChange,
  context: GroupChangeContext<G>
): GroupChangeOutcome<G, I, A, T> {
  const outcome = resolveGroupChange(state, change, context);
  if (outcome.state === state) {
    return outcome;
  }
  const synced = syncDefaultGroupMembership(context.family, outcome.state.groups, outcome.state.items);
  if (!synced.affectedItemIds.length) {
    return outcome;
  }
  return {
    ...outcome,
    affectedItemIds: [...new Set([...outcome.affectedItemIds, ...synced.affectedItemIds])],
    state: { ...outcome.state, items: synced.items }
  };
}
